/**
 * "Mochi for Files" scope (design `fm-on`): while a Files-area surface shows
 * its own Mochi — the floating launcher, the Create page, the voice call —
 * the global Mochi overlay steps aside, so there is only ever one Mochi on
 * screen (and one fewer WebGL context).
 */
import { useEffect, useSyncExternalStore } from "react";

let count = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function enterFmScope(): () => void {
  count += 1;
  if (typeof document !== "undefined") document.documentElement.classList.add("fm-on");
  notify();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    count = Math.max(0, count - 1);
    if (!count && typeof document !== "undefined") document.documentElement.classList.remove("fm-on");
    notify();
  };
}

/** Holds the scope while the calling component is mounted (and `active`). */
export function useFmScope(active = true): void {
  useEffect(() => (active ? enterFmScope() : undefined), [active]);
}

/** True while any Files-area Mochi is on screen. */
export function useInFmScope(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => count > 0,
    () => count > 0,
  );
}
